import { normalizeError, type BusinessErrorCode } from "@envirohub/utils";
import type { InvitationStatus } from "@/api/types";

/**
 * The invitations list is owner-only and the auth context has no owner flag,
 * so a FORBIDDEN from it means "not the owner" (IO-48).
 */
export function resolveInvitationsAccess(error: unknown): "forbidden" | "error" {
  return normalizeError(error).errorCode === "FORBIDDEN" ? "forbidden" : "error";
}

export function canRevokeInvitation(status: InvitationStatus): boolean {
  return status === "PENDING";
}

/**
 * The backend leaves an expired invitation `PENDING` with a past `expiresAt`;
 * show it as expired so the owner does not wait on a dead link.
 */
export function invitationDisplayStatus(
  invitation: { status: InvitationStatus; expiresAt: string },
  now: number = Date.now(),
): InvitationStatus {
  if (invitation.status !== "PENDING") return invitation.status;
  const expiresAt = Date.parse(invitation.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt <= now ? "EXPIRED" : "PENDING";
}

/** Accept-page link the owner shares; the backend sends no email. */
export function buildInvitationLink(origin: string, token: string): string {
  return `${origin.replace(/\/+$/, "")}/invite/accept?token=${encodeURIComponent(token)}`;
}

export function readInvitationToken(searchParams: URLSearchParams): string | null {
  const token = searchParams.get("token")?.trim();
  return token || null;
}

export type AcceptInvitationOutcome =
  "invalid" | "rolesInvalid" | "emailExists" | "fields" | "generic";

const INVALID_INVITATION_CODES: ReadonlyArray<BusinessErrorCode> = [
  "INVITATION_INVALID",
  "INVITATION_NOT_FOUND",
];

/** Decide how the accept page reacts to a failed `POST /auth/invitations/accept`. */
export function resolveAcceptInvitationError(
  action: { code: BusinessErrorCode | null; fieldErrors: Record<string, string> },
  formFields: readonly string[],
): AcceptInvitationOutcome {
  const { code, fieldErrors } = action;
  if (code && INVALID_INVITATION_CODES.includes(code)) return "invalid";
  if (code === "INVITATION_ROLES_INVALID") return "rolesInvalid";
  if (code === "EMAIL_ALREADY_EXISTS") return "emailExists";
  if (Object.keys(fieldErrors).some((field) => formFields.includes(field))) return "fields";
  return "generic";
}

/** Router state the accept page hands to `/login`. */
export interface InvitationLoginState {
  email: string;
  invitationAccepted: true;
}
